import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, Snackbar, Alert, Container, Breadcrumbs, Link } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import PropertyForm from '@/components/PropertyForm';
import { PropertyFormData } from '@/types';
import { PropertyService } from '@/services/propertyService';
import { useAuthStore } from '@/store/authStore';

const PropertyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chantierId = searchParams.get('chantierId');
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (data: PropertyFormData, files?: File[], documentFiles?: File[]) => {
    if (!user) {
      setError('You must be logged in to create a property');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If we have a chantierId, we add it to the property data
      const propertyData = {
        ...data,
        chantierId: chantierId || undefined
      };

      // 1. Create the property first to get an ID
      const newProperty = await PropertyService.createProperty(propertyData as any, user.id);

      let imageUrls: string[] = [];

      // 2. Upload images if any
      if (files && files.length > 0) {
        // Use Promise.all to upload images in parallel
        await Promise.all(
          files.map(async (file, index) => {
            try {
              const url = await PropertyService.uploadPropertyImage(newProperty.id, file, index);
              imageUrls.push(url);
            } catch (err) {
              console.error('Failed to upload image:', err);
              // Continue with other images
            }
          })
        );
      }

      let documentUrls: string[] = [];

      // 3. Upload documents if any
      if (documentFiles && documentFiles.length > 0) {
         await Promise.all(
             documentFiles.map(async (file, index) => {
                 try {
                     // Extract file extension or name as type
                     const docType = 'doc'; 
                     const url = await PropertyService.uploadPropertyDocument(newProperty.id, file, docType);
                     documentUrls.push(url);
                 } catch (err) {
                     console.error('Failed to upload document:', err);
                 }
             })
         );
      }

      // 4. Update property with image and document URLs if we have any
      if (imageUrls.length > 0 || documentUrls.length > 0) {
           const allImages = [...(newProperty.metadata.images || []), ...imageUrls];
           const allDocuments = [...(newProperty.metadata.documents || []), ...documentUrls];
           
           await PropertyService.updateProperty(newProperty.id, {
             metadata: {
               ...newProperty.metadata,
               images: allImages,
               documents: allDocuments
             }
           });
      }

      setSuccess(true);
      // Delay navigation slightly to show success message
      setTimeout(() => {
        if (chantierId) {
          navigate(`/chantiers/${chantierId}`);
        } else {
          navigate('/properties');
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error creating property:', err);
      setError(err.message || 'Failed to create property');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (chantierId) {
      navigate(`/chantiers/${chantierId}`);
    } else {
      navigate('/properties');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
          <Link 
            color="inherit" 
            href="/dashboard" 
            onClick={(e) => { 
              e.preventDefault(); 
              navigate('/dashboard'); 
            }}
            sx={{ cursor: 'pointer' }}
          >
            Dashboard
          </Link>
          {chantierId ? (
            <Link 
              color="inherit" 
              href={`/chantiers/${chantierId}`}
              onClick={(e) => { 
                e.preventDefault(); 
                navigate(`/chantiers/${chantierId}`); 
              }}
              sx={{ cursor: 'pointer' }}
            >
              Chantier View
            </Link>
          ) : (
            <Link 
              color="inherit" 
              href="/properties" 
              onClick={(e) => { 
                e.preventDefault(); 
                navigate('/properties'); 
              }}
              sx={{ cursor: 'pointer' }}
            >
              Properties
            </Link>
          )}
          <Typography color="text.primary">Create Property</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Create New Property
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Fill in the details below to list a new property.
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        <PropertyForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </Paper>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Property created successfully! Redirecting...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PropertyCreatePage;
